/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Api2LastValueData from '@/components/api/Api2LastValueData';
import TimeSeriesValueCard from '@/components/kpi/TimeSeriesValueCard';
import { useI18n } from '@/context/I18nContext';
import type { Api2ValueDataItem, Api2ValueDataValue } from '@/lib/overview/api2ValueData';
import type { TimeSeriesCalc, TimeSeriesTrafficLightConfig, TimeSeriesValueLabelsConfig } from '@/lib/timeSeries/timeSeriesValue';

interface KpiValueApi2LastValueProps {
    calc?: TimeSeriesCalc;
    calcCode?: string;
    conversion?: number;
    date: string;
    debug?: boolean;
    display?: {
        conversion?: number;
        fractionDigits?: number;
        offset?: number;
        series: string;
        unit?: string | null;
        valueLabels?: TimeSeriesValueLabelsConfig;
    };
    fractionDigits?: number;
    info?: string;
    link?: string;
    modal?: {
        buttonLabel?: string;
        message: string;
        title?: string;
    };
    offset?: number;
    series?: string;
    showLegend?: boolean;
    showDate?: boolean;
    title?: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    unit?: string | null;
    urn: string | string[];
    valueLabels?: TimeSeriesValueLabelsConfig;
}

function getItemValueBySeries<T>(
    item: Api2ValueDataItem | undefined,
    value: Api2ValueDataValue<T> | undefined,
    series: string | null,
): T | null {
    if (value === undefined || series === null) {
        return null;
    }

    if (!Array.isArray(value)) {
        return value;
    }

    const index = item?.urn.entries().findIndex((entry) => (
        entry.key === series || entry.aliases?.includes(series)
    )) ?? -1;

    return index >= 0 ? value[index] ?? null : null;
}

function KpiValueApi2LastValue({
    calc,
    calcCode,
    conversion = 1,
    date,
    debug = false,
    display,
    fractionDigits,
    info,
    link,
    modal,
    offset = 0,
    series,
    showLegend = false,
    showDate = false,
    title,
    trafficLight,
    unit,
    urn,
    valueLabels,
}: KpiValueApi2LastValueProps) {
    const { t } = useI18n();
    const requestedUrns = Array.isArray(urn)
        ? urn
        : urn.split(',').map((currentUrn) => currentUrn.trim()).filter(Boolean);
    const selectedSeries = series ?? requestedUrns[0] ?? null;
    const legendSensor = display?.series && requestedUrns.includes(display.series)
        ? display.series
        : series && requestedUrns.includes(series)
            ? series
            : requestedUrns[0];
    const requestUrns = [requestedUrns];

    return (
        <Api2LastValueData
            calc={calc}
            conversion={conversion}
            date={date}
            fractionDigits={fractionDigits}
            offset={offset}
            title={title}
            unit={unit}
            urn={requestUrns}
        >
            {({ items, isLoading, loadingLabel }) => {
                const item = items[0];
                const value = selectedSeries ? item?.rawValue.get(selectedSeries) ?? null : null;
                const selectedDisplaySeries = display?.series ?? selectedSeries;
                const displayValue = selectedDisplaySeries
                    ? item?.rawValue.get(selectedDisplaySeries) ?? null
                    : null;

                return (
                    <TimeSeriesValueCard
                        conversion={debug ? 1 : (display?.conversion ?? conversion)}
                        displayValue={debug && value !== null ? String(value) : null}
                        errorMessage={getItemValueBySeries(item, item?.errorMessage, selectedSeries) ?? ''}
                        fractionDigits={display?.fractionDigits ?? fractionDigits}
                        info={debug ? undefined : info}
                        isLoading={isLoading}
                        legendConversion={debug ? 1 : conversion}
                        legendCode={debug || !showLegend || !calc
                            ? undefined
                            : calcCode}
                        legendCodeMissingMessage={debug || !showLegend || !calc || calcCode
                            ? undefined
                            : t('The calculation code is not available for this calculated value.')}
                        legendFractionDigits={debug ? undefined : fractionDigits}
                        legendNote={debug || !showLegend || !calc
                            ? undefined
                            : t(calcCode
                                ? 'The displayed and classified value was calculated with "calc" as follows:'
                                : 'The displayed value was calculated with calc.')}
                        legendOffset={debug ? 0 : offset}
                        legendSensor={debug ? undefined : legendSensor}
                        legendUnit={debug ? null : (unit ?? null)}
                        loadingLabel={loadingLabel}
                        link={link}
                        metaDate={debug ? null : (showDate ? getItemValueBySeries(item, item?.date, selectedDisplaySeries) : null)}
                        modal={modal}
                        offset={debug ? 0 : (display?.offset ?? offset)}
                        showLegend={debug ? false : showLegend}
                        title={title}
                        trafficLight={debug ? undefined : trafficLight}
                        trafficLightValue={value}
                        unit={debug ? null : (display?.unit ?? unit)}
                        value={debug ? value : (display ? displayValue : value)}
                        valueLabels={debug ? undefined : (display?.valueLabels ?? valueLabels)}
                    />
                );
            }}
        </Api2LastValueData>
    );
}

export default KpiValueApi2LastValue;
